import { useState, useEffect, useCallback } from "react";

import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "examples/Breadcrumbs";
import {
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";
import axios from "../../../api";
import { userLogout } from "store/reducers/auth";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useSelector } from "react-redux";
import { Avatar, Typography, Badge } from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import styled from "@emotion/styled";
import NotificationDrawer, {
  useNotificationUnreadCount,
} from "components/NotificationDrawer";

const AvatarViewWrapper = styled("div")(({ theme }) => {
  return {
    cursor: "pointer",
    display: "flex",
  };
});
type DashboardNavbarProps = Readonly<{
  light?: boolean;
  isMini?: boolean;
}>;
function DashboardNavbar({ light, isMini }: DashboardNavbarProps) {
  const user = useSelector((state: any) => state.auth.user);
  const [controller, dispatch] = useSoftUIController();
  const {
    miniSidenav,
    fixedNavbar,
    // openConfigurator
  } = controller;
  const [openMenu, setOpenMenu] = useState<null | HTMLElement>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifRefreshTick, setNotifRefreshTick] = useState(0);
  const bumpNotif = useCallback(() => {
    setNotifRefreshTick((t) => t + 1);
  }, []);
  const { count: unreadNotif, refresh: refreshNotifCount } =
    useNotificationUnreadCount(bumpNotif);
  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }
    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleCloseMenu = () => {
    setOpenMenu(null);
  };
  const navigate = useNavigate();
  const handleLogout = async () => {
    await axios.get("/auth/logout");
    localStorage.removeItem("token");
    dispatch(userLogout());
    navigate("/home");
    window.location.reload();
  };
  const handleSwapBack = async () => {
    const swapData = {
      userId: user?.actualUserId,
      userName: user?.actualName,
      userEmail: user?.actualEmail,
      userRole: [user?.actualRole],
      userRoleId: user?.actualRoleId,
    };
    try {
      await axios.post("/auth/switch-back", swapData);
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  // Render the notifications menu

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    ></Menu>
  );

  return (
    <Toolbar sx={(theme) => navbarContainer(theme)}>
      <SoftBox
        className="home-icon"
        color="inherit"
        mb={{ xs: 1, md: 0 }}
        sx={(theme) => navbarRow(theme, { isMini })}
        onClick={() => navigate("/customer/profile")}
      >
        <Breadcrumbs icon="home" title={route} route={route} light={light} />
      </SoftBox>
      {isMini ? null : (
          <SoftBox
            sx={(theme) => navbarRow(theme, { isMini })}
            className="logout-icons"
          >
            <SoftBox color={light ? "white" : "inherit"} className="m-menu">
              <IconButton
                size="small"
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon className={light ? "text-white" : "text-dark"}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {renderMenu()}
            </SoftBox>
          <SoftBox
            className="profile-pic"
            color={light ? "white" : "inherit"}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {user && !user?.isSuperAdmin && user?.roleId !== 1 && (
              <>
                <IconButton
                  size="medium"
                  color="inherit"
                  aria-label="Notifications"
                  onClick={() => setNotifOpen(true)}
                  sx={{ mr: 0.5, p: 0.9 }}
                >
                  <Badge
                    badgeContent={unreadNotif > 0 ? unreadNotif : 0}
                    color="error"
                    max={99}
                    invisible={unreadNotif === 0}
                  >
                    <NotificationsNoneIcon
                      sx={(theme) => ({
                        fontSize: 30,
                        color: light
                          ? theme.palette.common.white
                          : theme.palette.text.primary,
                      })}
                    />
                  </Badge>
                </IconButton>
                <NotificationDrawer
                  open={notifOpen}
                  onClose={() => setNotifOpen(false)}
                  refreshSignal={notifRefreshTick}
                  onAfterRead={refreshNotifCount}
                />
              </>
            )}
            <AvatarViewWrapper>
                <Avatar
                  sx={{
                    width: { xs: 30, lg: 64 },
                    height: { xs: 30, lg: 64 },
                    cursor: "pointer",
                  }}
                  src={
                    user?.profileImage
                      ? `${process.env.REACT_APP_CLOUD_FRONT_URL}/${user?.profileImage}`
                      : undefined
                  }
                />
                <Typography variant="h6">{user?.name}</Typography>
              </AvatarViewWrapper>
              {user?.actualUserId && (
                <IconButton aria-label="edit" onClick={handleSwapBack}>
                  <SwapHorizIcon />
                </IconButton>
              )}
              <Link to="/">
                <IconButton sx={navbarIconButton} size="small">
                  <Icon
                    className="logout-btn"
                    sx={(theme) => ({
                      color: light
                        ? theme.palette.common.white
                        : theme.palette.text.primary,
                    })}
                    onClick={handleLogout}
                  >
                    logout
                  </Icon>
                </IconButton>
              </Link>
            </SoftBox>
          </SoftBox>
      )}
    </Toolbar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
